import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  LogOut,
  Send,
  Copy,
  Check,
  X,
  Share2,
  Download,
  Eye,
  Folder,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
  File as FileIcon,
  Users,
  UserPlus,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { ReplaysGrid } from "@/components/ReplaysGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_mlo_token";

// Same set the admin uses
const CATEGORIES = [
  { key: "marketing", label: "Marketing Assets" },
  { key: "messaging", label: "Messaging & Outreach" },
  { key: "process", label: "Process" },
  { key: "other", label: "Other Documents" },
  { key: "replays", label: "Replays" },
];

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconFor(ctype) {
  if (!ctype) return FileIcon;
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype.startsWith("video/")) return Film;
  if (ctype.startsWith("audio/")) return Music;
  if (ctype.includes("zip")) return Archive;
  if (
    ctype === "application/pdf" ||
    ctype.includes("word") ||
    ctype.includes("document") ||
    ctype.startsWith("text/") ||
    ctype.includes("presentation") ||
    ctype.includes("sheet")
  )
    return FileText;
  return FileIcon;
}

export default function MLOLibrary() {
  const nav = useNavigate();
  const [me, setMe] = useState({ loading: true, user: null, profile: null });
  const [tab, setTab] = useState("marketing");
  const [subcategories, setSubcategories] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeSubId, setActiveSubId] = useState("all");
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issueTarget, setIssueTarget] = useState(null);
  const [bdms, setBdms] = useState([]);

  const authHeader = useCallback(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mlo/auth/me`, { headers: authHeader() });
      setMe({ loading: false, user: data.user, profile: data.profile });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      nav("/mlologin", { replace: true });
    }
  }, [authHeader, nav]);

  const loadSubcategories = useCallback(async () => {
    if (tab === "replays") {
      setSubcategories([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/mlo/library/subcategories`, {
        params: { category: tab },
        headers: authHeader(),
      });
      setSubcategories(res.data || []);
    } catch (e) {
      console.warn("MLO library request failed:", e?.response?.status || e?.message);
    }
  }, [tab, authHeader]);

  const loadFolders = useCallback(async () => {
    if (!activeSubId || activeSubId === "all") {
      setFolders([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/mlo/library/folders`, {
        params: { subcategory_id: activeSubId },
        headers: authHeader(),
      });
      setFolders(res.data || []);
    } catch (e) {
      console.warn("MLO library request failed:", e?.response?.status || e?.message);
    }
  }, [activeSubId, authHeader]);

  const loadAssets = useCallback(async () => {
    if (tab === "replays") {
      setAssets([]);
      return;
    }
    setLoading(true);
    try {
      const params = { category: tab };
      if (activeSubId && activeSubId !== "all") params.subcategory_id = activeSubId;
      const res = await axios.get(`${API}/mlo/library/assets`, {
        params,
        headers: authHeader(),
      });
      setAssets(res.data || []);
    } catch (e) {
      console.warn("MLO library request failed:", e?.response?.status || e?.message);
    } finally {
      setLoading(false);
    }
  }, [tab, activeSubId, authHeader]);

  const loadBdms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mlo/bdms`, { headers: authHeader() });
      setBdms(res.data || []);
    } catch (e) {
      console.warn("MLO library request failed:", e?.response?.status || e?.message);
    }
  }, [authHeader]);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { if (me.user) loadBdms(); }, [me.user, loadBdms]);
  useEffect(() => { if (me.user) { loadSubcategories(); setActiveSubId("all"); } }, [tab, me.user, loadSubcategories]);
  useEffect(() => { if (me.user) { loadAssets(); loadFolders(); } }, [me.user, activeSubId, tab, loadAssets, loadFolders]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/mlologin", { replace: true });
  };

  if (me.loading) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  const activeSub = subcategories.find((s) => s.id === activeSubId);
  const activeCat = CATEGORIES.find((c) => c.key === tab);

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="mlo-library-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header
        className="text-white"
        style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 50%, #17385e 100%)" }}
      >
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Link to="/mlodashboard" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <Link to="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
            </Link>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#A6D24A]">Great Rate Check Partners</div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Your Marketing Toolkit
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-white/70">{bdms.length} BDM{bdms.length === 1 ? "" : "s"} referred</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-white/90"
              data-testid="mlo-library-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
        {/* Category tabs */}
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap gap-2 pb-5">
          {CATEGORIES.map((c) => {
            const active = c.key === tab;
            return (
              <button
                key={c.key}
                onClick={() => setTab(c.key)}
                data-testid={`mlo-lib-cat-${c.key}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  active
                    ? "bg-white text-[#0F2A47]"
                    : "bg-white/10 text-white/85 hover:bg-white/15"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-[#0F2A47]">{activeCat?.label}</h2>
          <p className="text-sm text-[#59564C]">
            {tab === "replays"
              ? "Recorded monthly partner meetings and short training clips — watch anytime."
              : "Browse and issue materials to your BDMs. Only the internal team can add/edit files — you can share what's already here."}
          </p>
        </div>

        {tab === "replays" ? (
          <ReplaysGrid getAuthHeader={authHeader} />
        ) : (
        <>
        {/* Subcategory chips */}
        <div className="mb-6 flex flex-wrap gap-2">
          <SubChip active={activeSubId === "all"} onClick={() => setActiveSubId("all")} testid="mlo-lib-sub-all">
            All
          </SubChip>
          {subcategories.map((s) => (
            <SubChip key={s.id} active={s.id === activeSubId} onClick={() => setActiveSubId(s.id)} testid={`mlo-lib-sub-${s.id}`}>
              {s.name}
            </SubChip>
          ))}
        </div>

        {/* Subcategory-level "Issue" button */}
        {activeSub && (
          <div className="mb-5 flex items-center gap-2">
            <button
              onClick={() => setIssueTarget({ kind: "subcategory", id: activeSub.id, name: activeSub.name })}
              data-testid="mlo-lib-issue-subcategory"
              className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-3 py-1.5 bg-[#0F2A47] text-white hover:bg-[#173a5f]"
            >
              <Send className="w-3.5 h-3.5" /> Send this whole subcategory to a BDM
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-20 flex items-center justify-center text-[#7A7668]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : activeSubId === "all" ? (
          <FlatGrid
            assets={assets}
            authHeader={authHeader}
            emptyLabel="Your admin will add files here."
          />
        ) : (
          <FolderGroups
            assets={assets}
            folders={folders}
            authHeader={authHeader}
            onIssueFolder={(f) => setIssueTarget({ kind: "folder", id: f.id, name: f.name })}
          />
        )}
        </>
        )}
      </main>

      <AnimatePresence>
        {issueTarget && (
          <MLOIssueModal
            target={issueTarget}
            bdms={bdms}
            authHeader={authHeader}
            onClose={() => setIssueTarget(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Subcategory chip ----------
function SubChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? "bg-[#0F2A47] text-white"
          : "bg-white text-[#0F2A47] border border-[#EDE9DC] hover:border-[#1F8F8B]"
      }`}
    >
      {children}
    </button>
  );
}

// ---------- Flat grid (for "All") ----------
function FlatGrid({ assets, authHeader, emptyLabel }) {
  if (assets.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-[#D9D3BF] bg-white/60 py-16 text-center">
        <FileText className="w-8 h-8 text-[#7A7668] mx-auto mb-2" />
        <p className="text-[#0F2A47] font-medium">Nothing here yet.</p>
        <p className="text-sm text-[#7A7668] mt-1">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {assets.map((a) => (
        <ReadOnlyAssetCard key={a.id} asset={a} authHeader={authHeader} />
      ))}
    </div>
  );
}

// ---------- Folder-grouped view ----------
function FolderGroups({ assets, folders, authHeader, onIssueFolder }) {
  const [expanded, setExpanded] = useState({});
  return (
    <div className="space-y-6">
      {folders.map((folder) => {
        const items = assets.filter((a) => a.folder_id === folder.id);
        const isOpen = expanded[folder.id] !== false;
        return (
          <section key={folder.id} data-testid={`mlo-lib-folder-${folder.id}`} className="rounded-2xl border border-[#EDE9DC] bg-white/60 overflow-hidden">
            <header className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-white border-b border-[#EDE9DC]">
              <button
                onClick={() => setExpanded((p) => ({ ...p, [folder.id]: !isOpen }))}
                className="inline-flex items-center gap-2 text-[#0F2A47] font-semibold text-sm hover:text-[#1F8F8B]"
              >
                <span className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>▸</span>
                <Folder className="w-4 h-4 text-[#1F8F8B]" />
                {folder.name}
                <span className="text-[11px] font-normal text-[#7A7668]">({items.length})</span>
              </button>
              <button
                onClick={() => onIssueFolder(folder)}
                data-testid={`mlo-lib-folder-issue-${folder.id}`}
                className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#A6D24A] text-[#0F2A47] hover:bg-[#94bd41]"
              >
                <Send className="w-3 h-3" /> Send to BDM
              </button>
            </header>
            {isOpen && (
              <div className="p-4 sm:p-5">
                {items.length === 0 ? (
                  <p className="text-xs text-[#7A7668] italic">This folder is empty.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((a) => (
                      <ReadOnlyAssetCard key={a.id} asset={a} authHeader={authHeader} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}

      {(() => {
        const ungrouped = assets.filter((a) => !a.folder_id);
        if (ungrouped.length === 0 && folders.length > 0) return null;
        const isOpen = expanded.ungrouped !== false;
        return (
          <section data-testid="mlo-lib-folder-ungrouped" className="rounded-2xl border border-[#EDE9DC] bg-white/60 overflow-hidden">
            <header className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-white border-b border-[#EDE9DC]">
              <button
                onClick={() => setExpanded((p) => ({ ...p, ungrouped: !isOpen }))}
                className="inline-flex items-center gap-2 text-[#0F2A47] font-semibold text-sm hover:text-[#1F8F8B]"
              >
                <span className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>▸</span>
                Ungrouped
                <span className="text-[11px] font-normal text-[#7A7668]">({ungrouped.length})</span>
              </button>
            </header>
            {isOpen && (
              <div className="p-4 sm:p-5">
                {ungrouped.length === 0 ? (
                  <p className="text-xs text-[#7A7668] italic">Everything's organized into folders.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {ungrouped.map((a) => (
                      <ReadOnlyAssetCard key={a.id} asset={a} authHeader={authHeader} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })()}
    </div>
  );
}

// ---------- Read-only asset card for MLOs ----------
function ReadOnlyAssetCard({ asset, authHeader }) {
  const Icon = iconFor(asset.content_type);
  const isImage = (asset.content_type || "").startsWith("image/");
  const ext = (asset.filename?.split(".").pop() || (asset.content_type || "").split("/").pop() || "file")
    .toUpperCase()
    .slice(0, 5);

  // Load an authenticated blob (image or thumb) as a blob URL so the <img>
  // tag works without leaking bearer tokens or exposing a public endpoint.
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let objectUrl = null;
    const url = isImage
      ? `${API}/library/assets/${asset.id}/download`
      : `${API}/library/assets/${asset.id}/thumb`;
    (async () => {
      try {
        const resp = await fetch(url, { headers: authHeader() });
        if (!resp.ok) return;
        const b = await resp.blob();
        if (revoked) return;
        objectUrl = URL.createObjectURL(b);
        setBlobUrl(objectUrl);
      } catch (e) {
        console.debug("Thumbnail unavailable, using icon placeholder:", e?.message);
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, isImage, authHeader]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden bg-white border border-[#EDE9DC] hover:shadow-md transition"
      data-testid={`mlo-lib-asset-${asset.id}`}
    >
      <div className="w-full aspect-[16/10] bg-[#F4F2EB] flex items-center justify-center relative overflow-hidden">
        {blobUrl ? (
          <img
            src={blobUrl}
            alt={asset.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Icon className="w-12 h-12 text-[#7A7668]" strokeWidth={1.4} />
            <span className="text-[10px] uppercase tracking-widest text-[#7A7668]">{ext}</span>
          </div>
        )}
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest bg-white/90 px-2 py-1 rounded-md text-[#0F2A47]">
          {ext}
        </span>
      </div>
      <div className="p-4">
        <p className="text-sm font-semibold text-[#0F2A47] truncate" title={asset.filename}>
          {asset.filename}
        </p>
        {asset.description ? (
          <p className="text-xs text-[#59564C] mt-1 line-clamp-2">{asset.description}</p>
        ) : null}
        <div className="text-[11px] text-[#7A7668] mt-2">{fmtBytes(asset.size)}</div>
      </div>
    </motion.div>
  );
}

// ---------- Issue modal ----------
function MLOIssueModal({ target, bdms, authHeader, onClose }) {
  const [tab, setTab] = useState(bdms.length > 0 ? "bdms" : "manual");
  const [selectedIds, setSelectedIds] = useState({}); // by bdm id
  const [manualEmails, setManualEmails] = useState("");
  const [note, setNote] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { sent_to, share_url, sent_as }
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [excludedCount, setExcludedCount] = useState(0);

  useEffect(() => {
    if (target.kind !== "subcategory") return;
    axios
      .get(`${API}/mlo/library/subcategories/${target.id}/uncategorized-count`, { headers: authHeader() })
      .then((r) => setExcludedCount(r.data?.uncategorized_count || 0))
      .catch(() => {});
  }, [target, authHeader]);

  const kindLabel = target.kind === "folder" ? "Folder" : "Subcategory";

  const chosenRecipients = useMemo(() => {
    const list = [];
    if (tab === "bdms") {
      bdms.forEach((b) => {
        if (selectedIds[b.id] && b.email) list.push(b.email);
      });
    } else {
      manualEmails
        .split(/[\s,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((e) => list.push(e));
    }
    // dedupe
    return Array.from(new Set(list.map((e) => e.toLowerCase())));
  }, [tab, selectedIds, manualEmails, bdms]);

  const send = async () => {
    if (chosenRecipients.length === 0) {
      setErr(tab === "bdms" ? "Pick at least one BDM." : "Enter at least one email.");
      return;
    }
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const url = target.kind === "folder"
        ? `${API}/mlo/library/folders/${target.id}/issue`
        : `${API}/mlo/library/subcategories/${target.id}/issue`;
      const res = await axios.post(
        url,
        { recipients: chosenRecipients, note, expires_days: Number(days) || 30 },
        { headers: authHeader() },
      );
      setResult(res.data);
      toast.success(`Sent to ${res.data.sent_to.length} recipient${res.data.sent_to.length === 1 ? "" : "s"}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!result?.share_url) return;
    try {
      await navigator.clipboard.writeText(result.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("Clipboard copy failed:", e?.message);
    }
  };

  const toggleBdm = (id) => setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  const selectAll = () => {
    const next = {};
    bdms.forEach((b) => { next[b.id] = true; });
    setSelectedIds(next);
  };
  const clearAll = () => setSelectedIds({});

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      data-testid="mlo-issue-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#EDE9DC] flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#A6D24A]">Send {kindLabel}</div>
            <div className="text-lg font-semibold text-[#0F2A47] mt-0.5">{target.name}</div>
            <p className="text-xs text-[#7A7668] mt-1">
              The recipient sees a private link with previews & downloads. Email is sent from Great Rate Check but signed with your name and contact info.
            </p>
            {excludedCount > 0 && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid="mlo-issue-excluded-note">
                {excludedCount} uncategorized file{excludedCount !== 1 ? "s" : ""} won't be included — only files inside folders go out.
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F4F2EB]" data-testid="mlo-issue-close">
            <X className="w-4 h-4 text-[#59564C]" />
          </button>
        </div>

        {result ? (
          <div className="px-6 py-6 space-y-4">
            <div className="rounded-2xl bg-[#A6D24A]/10 border border-[#A6D24A]/40 p-4">
              <div className="flex items-center gap-2 text-[#0F2A47] font-semibold">
                <Check className="w-5 h-5" /> Sent as {result.sent_as}
              </div>
              <p className="text-sm text-[#59564C] mt-1">
                Delivered to {result.sent_to.length} recipient{result.sent_to.length === 1 ? "" : "s"}:
              </p>
              <p className="text-[12px] text-[#0F2A47] mt-1 break-all">{result.sent_to.join(", ")}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#59564C] mb-1">Share link (in case you want to send it another way)</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={result.share_url}
                  className="flex-1 rounded-lg border border-[#E6E2D6] bg-[#FDFBF7] px-3 py-2 text-xs text-[#0F2A47] focus:outline-none"
                  data-testid="mlo-issue-result-link"
                />
                <button
                  onClick={copy}
                  className="inline-flex items-center gap-1 rounded-lg bg-[#1F8F8B] hover:bg-[#177977] text-white px-3 py-2 text-xs"
                  data-testid="mlo-issue-result-copy"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white py-2.5 text-sm font-medium"
              data-testid="mlo-issue-done"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4 flex items-center gap-2 border-b border-[#EDE9DC]">
              <button
                onClick={() => setTab("bdms")}
                data-testid="mlo-issue-tab-bdms"
                className={`px-3 py-2 text-sm rounded-t-lg border-b-2 ${tab === "bdms" ? "border-[#1F8F8B] text-[#0F2A47] font-semibold" : "border-transparent text-[#7A7668]"}`}
              >
                <Users className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> My BDMs ({bdms.length})
              </button>
              <button
                onClick={() => setTab("manual")}
                data-testid="mlo-issue-tab-manual"
                className={`px-3 py-2 text-sm rounded-t-lg border-b-2 ${tab === "manual" ? "border-[#1F8F8B] text-[#0F2A47] font-semibold" : "border-transparent text-[#7A7668]"}`}
              >
                <UserPlus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Type emails
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
              {tab === "bdms" ? (
                bdms.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-[#D9D3BF] p-6 text-center bg-[#FDFBF7]">
                    <Users className="w-6 h-6 text-[#7A7668] mx-auto mb-2" />
                    <p className="text-sm text-[#0F2A47] font-medium">No BDMs yet</p>
                    <p className="text-xs text-[#7A7668] mt-1">Once BDMs apply through your <span className="font-mono">/{"{"}your-slug{"}"}/partnerapply</span> page, they'll show up here. For now, use the "Type emails" tab.</p>
                    <button onClick={() => setTab("manual")} className="mt-3 inline-flex text-xs text-[#1F8F8B] font-medium">Type emails instead →</button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-[11px] text-[#7A7668]">
                      <span>Pick recipients below.</span>
                      <div className="flex gap-2">
                        <button onClick={selectAll} className="text-[#1F8F8B] hover:underline">Select all</button>
                        <button onClick={clearAll} className="text-[#7A7668] hover:underline">Clear</button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {bdms.map((b) => {
                        const active = !!selectedIds[b.id];
                        return (
                          <label
                            key={b.id}
                            data-testid={`mlo-issue-bdm-${b.id}`}
                            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                              active ? "border-[#1F8F8B] bg-[#1F8F8B]/5" : "border-[#EDE9DC] hover:border-[#1F8F8B]/60"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => toggleBdm(b.id)}
                              className="mt-1 accent-[#1F8F8B]"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-[#0F2A47] truncate">{b.full_name || "Unnamed BDM"}</div>
                              <div className="text-[11px] text-[#59564C] truncate">{b.email}</div>
                              {(b.company || b.city_state) && (
                                <div className="text-[11px] text-[#7A7668] mt-0.5 truncate">
                                  {[b.company, b.city_state].filter(Boolean).join(" · ")}
                                </div>
                              )}
                            </div>
                            {b.classification && (
                              <span className="shrink-0 text-[10px] uppercase tracking-widest bg-[#F4F2EB] text-[#59564C] px-2 py-0.5 rounded-md">
                                {b.classification}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </>
                )
              ) : (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Recipient emails</span>
                  <textarea
                    value={manualEmails}
                    onChange={(e) => setManualEmails(e.target.value)}
                    rows={4}
                    placeholder="One per line, or comma-separated"
                    data-testid="mlo-issue-manual-emails"
                    className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                  />
                </label>
              )}

              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Personal note (optional)</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. Here's your monthly packet — let me know if you want a custom follow-up."
                  data-testid="mlo-issue-note"
                  className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                />
              </label>
              <label className="block max-w-[180px]">
                <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Link expires (days)</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  data-testid="mlo-issue-days"
                  className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                />
              </label>
            </div>

            <div className="px-6 py-4 border-t border-[#EDE9DC] flex items-center gap-3 bg-[#FDFBF7]">
              <div className="text-[11px] text-[#7A7668] flex-1">
                {chosenRecipients.length > 0 ? `${chosenRecipients.length} recipient${chosenRecipients.length === 1 ? "" : "s"} ready` : "No recipients yet"}
              </div>
              <button
                onClick={send}
                disabled={busy || chosenRecipients.length === 0}
                data-testid="mlo-issue-send"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-4 py-2.5 text-sm font-medium disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
            </div>
            {err && <p className="px-6 pb-4 text-sm text-red-600">{err}</p>}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
